import type { MoveId } from '@goaltree/backend/convex/lib/exerciseMoves'
import { type Frame, HEAD_R, figureFor, pts } from '@goaltree/backend/convex/lib/moveFigures'
import type { ItemKind } from '@goaltree/backend/convex/lib/sessionPlan'
import { cn } from '@goaltree/ui/lib/utils'
import { Dumbbell, Route, Timer } from 'lucide-react'

import { useReducedMotion } from '@/components/breath-pacer'

// An exercise's movement as a looping stick figure (SMIL), in currentColor with
// the accent for props. Reduced motion shows one still pose.

const KIND_ICON = { sets: Dumbbell, timed: Timer, distance: Route } as const
const EASE = '0.45 0 0.55 1'

type Part = (f: Frame) => string

const PARTS: [string, Part, boolean][] = [
  ['armF', (f) => pts(f.arms[1]), true],
  ['legF', (f) => pts(f.legs[1]), true],
  ['spine', (f) => pts(f.spine), false],
  ['legN', (f) => pts(f.legs[0]), false],
  ['armN', (f) => pts(f.arms[0]), false],
]

export function MoveFigure({
  move,
  kind,
  animate = true,
  className,
}: {
  move: MoveId
  kind: ItemKind
  animate?: boolean
  className?: string
}) {
  const reduce = useReducedMotion()
  const fig = figureFor(move)
  if (!fig) {
    const Icon = KIND_ICON[kind]
    return (
      <div className={cn('flex items-center justify-center', className)} aria-hidden>
        <Icon
          className={cn(
            'size-1/3 text-[var(--habit-accent,var(--primary))]',
            animate && !reduce && 'animate-pulse',
          )}
          strokeWidth={1.8}
        />
      </div>
    )
  }
  const live = animate && !reduce
  const still = fig.frames[fig.still]
  const loop = [...fig.frames, fig.frames[0]]
  const keyTimes = loop.map((_, i) => (i / (loop.length - 1)).toFixed(3)).join(';')
  const splines = Array.from({ length: loop.length - 1 }, () => EASE).join(';')
  const anim = (attr: string, values: string) =>
    live && (
      <animate
        attributeName={attr}
        values={values}
        keyTimes={keyTimes}
        calcMode="spline"
        keySplines={splines}
        dur={`${fig.dur}s`}
        repeatCount="indefinite"
      />
    )
  const far = fig.front ? 1 : 0.35
  return (
    <svg viewBox="0 0 100 100" className={className} aria-hidden>
      {fig.props?.map((p, i) =>
        p.kind === 'line' ? (
          <line
            key={i}
            x1={p.from[0]}
            y1={p.from[1]}
            x2={p.to[0]}
            y2={p.to[1]}
            className="stroke-[var(--habit-accent,var(--primary))]"
            strokeOpacity={0.55}
            strokeWidth={2}
            strokeLinecap="round"
          />
        ) : p.kind === 'circle' ? (
          <circle
            key={i}
            cx={p.at[0]}
            cy={p.at[1]}
            r={p.r}
            fill="none"
            className="stroke-[var(--habit-accent,var(--primary))]"
            strokeOpacity={0.7}
            strokeWidth={2}
          />
        ) : null,
      )}
      {PARTS.map(([key, part, isFar]) => (
        <polyline
          key={key}
          points={part(still)}
          fill="none"
          stroke="currentColor"
          strokeOpacity={isFar ? far : 1}
          strokeWidth={3.2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          {anim('points', loop.map(part).join(';'))}
        </polyline>
      ))}
      <circle cx={still.head[0]} cy={still.head[1]} r={HEAD_R} fill="currentColor">
        {anim('cx', loop.map((f) => f.head[0]).join(';'))}
        {anim('cy', loop.map((f) => f.head[1]).join(';'))}
      </circle>
      {fig.props?.map(
        (p, i) =>
          p.kind === 'plate' && (
            <circle
              key={`p${i}`}
              cx={still.arms[p.arm][2][0]}
              cy={still.arms[p.arm][2][1]}
              r={4.5}
              className="fill-[var(--habit-accent,var(--primary))]"
            >
              {anim('cx', loop.map((f) => f.arms[p.arm][2][0]).join(';'))}
              {anim('cy', loop.map((f) => f.arms[p.arm][2][1]).join(';'))}
            </circle>
          ),
      )}
    </svg>
  )
}
